import { useEffect, useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent,
  DropdownMenuCheckboxItem, DropdownMenuLabel, DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";
import { Plus, EnvelopeSimple, Trash, CheckCircle, Clock, ShieldStar, X, CaretDown, PencilSimple, Check } from "@phosphor-icons/react";
import { toast } from "sonner";
import ExportButton from "@/components/ExportButton";
import { USER_COLUMNS } from "@/lib/exportColumns";
import { useConfirm } from "@/components/ConfirmDialog";

const ROLE_LABEL = {
  super_admin: "Super Admin",
  admin: "Admin",
  poc: "POC",
};

const ROLE_DOT = {
  super_admin: "bg-violet-500",
  admin: "bg-blue-500",
  poc: "bg-emerald-500",
};

export default function UsersTab() {
  const { user: me } = useAuth();
  const confirm = useConfirm();
  const [users, setUsers] = useState([]);
  const [invites, setInvites] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showInvite, setShowInvite] = useState(false);
  const [pocNames, setPocNames] = useState([]);

  useEffect(() => {
    // Dynamic POC list — pulled from the same /meta/options endpoint as every
    // other page so deletions/additions in Manage POCs show up immediately.
    api.get("/meta/options").then((r) => setPocNames(r.data.pocs || []));
  }, []);

  const refresh = async () => {
    setLoading(true);
    try {
      const [u, i] = await Promise.all([
        api.get("/admin/users"),
        api.get("/admin/invites"),
      ]);
      setUsers(u.data.users);
      setInvites(i.data.invites.filter((x) => !x.accepted_at));
    } catch (e) {
      toast.error("Failed to load users");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    refresh();
  }, []);

  const removeUser = async (u) => {
    if (!await confirm({
      title: "Remove user?",
      body: `${u.email} will lose access immediately. This cannot be undone.`,
      confirmLabel: "Remove user",
      destructive: true,
    })) return;
    try {
      await api.delete(`/admin/users/${u.user_id}`);
      toast.success(`${u.email} removed`);
      refresh();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed");
    }
  };

  const saveName = async (u, newName) => {
    const trimmed = (newName || "").trim();
    if (!trimmed) {
      toast.error("Name cannot be empty");
      return false;
    }
    // NOTE: we deliberately do NOT short-circuit when trimmed === u.name.
    // Re-saving the same name is the supported way to trigger the cascade
    // backfill for records that were written before the rename existed
    // (e.g. old call logs / notes / tasks still showing the previous name).
    try {
      const r = await api.patch(`/admin/users/${u.user_id}/name`, { name: trimmed });
      const c = r.data?.cascaded || {};
      const totalHistorical =
        (c.call_logs || 0) +
        (c.notes || 0) +
        (c.tasks_assignee || 0) +
        (c.tasks_assigner || 0) +
        (c.tasks_completer || 0);
      const suffix = totalHistorical > 0
        ? ` · updated ${totalHistorical} historical record${totalHistorical === 1 ? "" : "s"}`
        : "";
      toast.success(`Renamed to "${trimmed}"${suffix}`);
      setUsers((prev) => prev.map((x) => x.user_id === u.user_id ? { ...x, name: trimmed } : x));
      return true;
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to rename");
      return false;
    }
  };

  const revokeInvite = async (inv) => {
    if (!await confirm({
      title: "Revoke invite?",
      body: `${inv.email} will no longer be able to use this invite link.`,
      confirmLabel: "Revoke invite",
      destructive: true,
    })) return;
    try {
      await api.delete(`/admin/invites/${inv.invite_id}`);
      toast.success(`Invite revoked`);
      refresh();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed");
    }
  };

  return (
    <div className="space-y-6" data-testid="users-tab">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="font-display text-lg font-semibold text-gray-900">Users & Access</h2>
          <p className="text-sm text-gray-500 mt-0.5">
            Invite by Gmail. Roles: <span className="font-mono text-xs">super_admin</span> ·{" "}
            <span className="font-mono text-xs">admin</span> · <span className="font-mono text-xs">poc</span>.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <ExportButton
            filename="admin-users"
            sheetName="Users"
            columns={USER_COLUMNS}
            rows={users}
            testId="export-admin-users"
          />
          <Button onClick={() => setShowInvite(true)} className="gap-2" data-testid="invite-user-btn">
            <Plus size={16} />
            Invite user
          </Button>
        </div>
      </div>

      {/* Active users */}
      <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
        <div className="px-4 py-3 border-b border-gray-100 flex items-center justify-between">
          <div>
            <div className="font-display text-sm font-semibold text-gray-900">
              Active users
              <span className="ml-2 text-[11px] font-mono text-gray-500">{users.length}</span>
            </div>
          </div>
        </div>
        <table className="w-full text-sm">
          <thead className="bg-gray-50 border-b border-gray-200 text-left">
            <tr>
              <Th>Email</Th><Th>Name</Th><Th>Role</Th><Th>POC</Th><Th>Invited by</Th><th></th>
            </tr>
          </thead>
          <tbody>
            {loading && <tr><td colSpan={6} className="px-4 py-8 text-center text-sm text-gray-400">Loading…</td></tr>}
            {!loading && users.length === 0 && (
              <tr><td colSpan={6} className="px-4 py-8 text-center text-sm text-gray-400">No users yet.</td></tr>
            )}
            {!loading && users.map((u) => {
              const isSuper = u.role === "super_admin";
              const isSelf = u.email === me?.email;
              const canEditName = me?.role === "super_admin";
              return (
                <tr key={u.user_id} className="border-b border-gray-100" data-testid={`user-row-${u.email}`}>
                  <td className="px-4 py-3 font-mono text-xs">{u.email}</td>
                  <td className="px-4 py-3">
                    <NameCell user={u} canEdit={canEditName} onSave={saveName} />
                  </td>
                  <td className="px-4 py-3">
                    <span className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full bg-gray-50 border border-gray-200 text-xs">
                      <span className={`w-1.5 h-1.5 rounded-full ${ROLE_DOT[u.role]}`} />
                      {ROLE_LABEL[u.role] || u.role}
                      {isSuper && <ShieldStar size={11} weight="fill" className="text-violet-600 ml-0.5" />}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-xs text-gray-600"><PocCell user={u} /></td>
                  <td className="px-4 py-3 text-xs text-gray-500 font-mono">{u.invited_by || "—"}</td>
                  <td className="px-4 py-3 text-right">
                    <Button
                      variant="ghost"
                      size="sm"
                      disabled={isSuper || isSelf}
                      onClick={() => removeUser(u)}
                      className="text-red-600 hover:text-red-700 hover:bg-red-50 h-7 px-2"
                      data-testid={`user-remove-${u.email}`}
                    >
                      <Trash size={14} />
                    </Button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Pending invites */}
      <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
        <div className="px-4 py-3 border-b border-gray-100">
          <div className="font-display text-sm font-semibold text-gray-900">
            Pending invites
            <span className="ml-2 text-[11px] font-mono text-gray-500">{invites.length}</span>
          </div>
        </div>
        {invites.length === 0 ? (
          <div className="px-4 py-8 text-center text-sm text-gray-400">No pending invites.</div>
        ) : (
          <table className="w-full text-sm">
            <thead className="bg-gray-50 border-b border-gray-200 text-left">
              <tr><Th>Email</Th><Th>Role</Th><Th>POC</Th><Th>Sent</Th><Th>By</Th><th></th></tr>
            </thead>
            <tbody>
              {invites.map((inv) => (
                <tr key={inv.invite_id} className="border-b border-gray-100" data-testid={`invite-row-${inv.email}`}>
                  <td className="px-4 py-3 font-mono text-xs">
                    <span className="inline-flex items-center gap-2">
                      <Clock size={13} className="text-amber-500" />
                      {inv.email}
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    <span className="inline-flex items-center gap-1.5 text-xs">
                      <span className={`w-1.5 h-1.5 rounded-full ${ROLE_DOT[inv.role]}`} />
                      {ROLE_LABEL[inv.role]}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-xs text-gray-600"><PocCell user={inv} /></td>
                  <td className="px-4 py-3 text-xs text-gray-500 font-mono">
                    {inv.created_at ? new Date(inv.created_at).toLocaleDateString() : "—"}
                  </td>
                  <td className="px-4 py-3 text-xs text-gray-500 font-mono">{inv.invited_by}</td>
                  <td className="px-4 py-3 text-right">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => revokeInvite(inv)}
                      className="text-red-600 hover:text-red-700 hover:bg-red-50 h-7 px-2"
                      data-testid={`invite-revoke-${inv.email}`}
                    >
                      <X size={14} />
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      <InviteDialog
        open={showInvite}
        onOpenChange={setShowInvite}
        pocNames={pocNames}
        onCreated={() => {
          setShowInvite(false);
          refresh();
        }}
      />
    </div>
  );
}

function InviteDialog({ open, onOpenChange, onCreated, pocNames }) {
  const [email, setEmail] = useState("");
  const [role, setRole] = useState("poc");
  // Multi-POC support: store selected POCs as a Set for O(1) toggles.
  const [selectedPocs, setSelectedPocs] = useState(() => new Set());
  const [submitting, setSubmitting] = useState(false);

  // Default to the first POC whenever the dropdown list arrives and the user
  // hasn't picked anything yet (and reset selection each time the dialog opens).
  useEffect(() => {
    if (open && (pocNames || []).length > 0 && selectedPocs.size === 0) {
      setSelectedPocs(new Set([pocNames[0]]));
    }
  }, [open, pocNames, selectedPocs.size]);

  const reset = () => {
    setEmail("");
    setRole("poc");
    setSelectedPocs(new Set((pocNames || [])[0] ? [pocNames[0]] : []));
  };

  const togglePoc = (name) => {
    setSelectedPocs((prev) => {
      const next = new Set(prev);
      if (next.has(name)) {
        next.delete(name);
      } else {
        next.add(name);
      }
      return next;
    });
  };

  const pocList = Array.from(selectedPocs);
  // Trigger label stays compact regardless of how many POCs are picked.
  // Caveat: the chip list under the trigger already shows every selected name,
  // so the label only needs to summarise the count + a teaser.
  const triggerLabel = (() => {
    if (pocList.length === 0) return "Select POC(s)";
    if (pocList.length === 1) return pocList[0];
    if (pocList.length <= 3) return `${pocList.length} POCs · ${pocList.join(", ")}`;
    return `${pocList.length} POCs · ${pocList.slice(0, 2).join(", ")}, +${pocList.length - 2} more`;
  })();

  const submit = async () => {
    if (!email || !email.includes("@")) {
      toast.error("Enter a valid email");
      return;
    }
    if (role === "poc" && pocList.length === 0) {
      toast.error("Pick at least one POC");
      return;
    }
    setSubmitting(true);
    try {
      const res = await api.post("/admin/users/invite", {
        email: email.trim().toLowerCase(),
        role,
        poc_names: role === "poc" ? pocList : null,
      });
      if (res.data.email_sent) {
        toast.success(`Invite sent to ${email}`);
      } else {
        toast.warning(`Invite saved but email delivery failed: ${res.data.email_error || "unknown error"}`);
      }
      reset();
      onCreated();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Invite failed");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) reset(); onOpenChange(v); }}>
      <DialogContent className="max-w-md" data-testid="invite-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <EnvelopeSimple size={18} className="text-gray-700" />
            Invite a user
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-4 mt-2">
          <div>
            <Label htmlFor="invite-email">Gmail address</Label>
            <Input
              id="invite-email"
              type="email"
              placeholder="someone@example.com"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              data-testid="invite-email-input"
            />
          </div>
          <div>
            <Label>Role</Label>
            <Select value={role} onValueChange={setRole}>
              <SelectTrigger data-testid="invite-role-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="admin">
                  <div className="flex items-center gap-2">
                    <span className="w-1.5 h-1.5 rounded-full bg-blue-500" />
                    Admin — full access + can invite others
                  </div>
                </SelectItem>
                <SelectItem value="poc">
                  <div className="flex items-center gap-2">
                    <span className="w-1.5 h-1.5 rounded-full bg-emerald-500" />
                    POC — read-only, scoped to selected POC's firms
                  </div>
                </SelectItem>
              </SelectContent>
            </Select>
          </div>
          {role === "poc" && (
            <div>
              <Label>Which POC(s)?</Label>
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <button
                    type="button"
                    className="w-full mt-1 flex items-center justify-between px-3 py-2 text-sm border border-gray-200 rounded-md bg-white hover:border-gray-300 focus:outline-none focus:ring-2 focus:ring-violet-100 focus:border-violet-300"
                    data-testid="invite-poc-trigger"
                  >
                    <span className={`min-w-0 flex-1 text-left truncate ${pocList.length === 0 ? "text-gray-400" : "text-gray-900"}`}>
                      {triggerLabel}
                    </span>
                    <CaretDown size={14} className="text-gray-400 shrink-0 ml-2" />
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent className="w-[--radix-dropdown-menu-trigger-width]" align="start">
                  <DropdownMenuLabel className="text-[10px] uppercase tracking-wider text-gray-500 font-medium">
                    Tick all POCs this user should see
                  </DropdownMenuLabel>
                  <DropdownMenuSeparator />
                  {(pocNames || []).length === 0 && (
                    <div className="px-3 py-4 text-xs text-gray-400 text-center">Loading…</div>
                  )}
                  {(pocNames || []).map((p) => (
                    <DropdownMenuCheckboxItem
                      key={p}
                      checked={selectedPocs.has(p)}
                      onCheckedChange={() => togglePoc(p)}
                      onSelect={(e) => e.preventDefault()}
                      data-testid={`invite-poc-option-${p}`}
                    >
                      {p}
                    </DropdownMenuCheckboxItem>
                  ))}
                </DropdownMenuContent>
              </DropdownMenu>
              {pocList.length > 0 && (
                <div className="mt-2 flex flex-wrap gap-1.5" data-testid="invite-poc-chips">
                  {pocList.map((p) => (
                    <span
                      key={p}
                      className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-emerald-50 border border-emerald-200 text-[11px] text-emerald-700"
                    >
                      {p}
                      <button
                        type="button"
                        onClick={() => togglePoc(p)}
                        className="text-emerald-600 hover:text-emerald-800"
                        data-testid={`invite-poc-chip-remove-${p}`}
                      >
                        <X size={9} weight="bold" />
                      </button>
                    </span>
                  ))}
                </div>
              )}
              <p className="mt-2 text-[11px] text-gray-500">
                {pocList.length <= 1
                  ? `This user will only see firms where poc = ${pocList[0] || "—"}.`
                  : `This user will see firms assigned to any of: ${pocList.join(", ")}.`}
              </p>
            </div>
          )}
          <div className="text-[11px] text-gray-500 bg-gray-50 border border-gray-200 rounded-md p-2.5 flex items-start gap-2">
            <CheckCircle size={13} className="text-emerald-600 mt-0.5 shrink-0" />
            We'll send an invitation email via Resend with a sign-in link. The user
            must use this exact Gmail address to sign in.
          </div>
        </div>
        <DialogFooter className="mt-4">
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={submit} disabled={submitting} data-testid="invite-send-btn">
            {submitting ? "Sending…" : "Send invite"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// Renders the POC column for a user/invite row.
// Supports both `poc_names: ["Shankar","Onkar"]` and legacy `poc_name`.
function PocCell({ user }) {
  const names = (user.poc_names && user.poc_names.length > 0)
    ? user.poc_names
    : (user.poc_name ? [user.poc_name] : []);
  if (names.length === 0) return <span className="text-gray-400">—</span>;
  if (names.length === 1) return <span>{names[0]}</span>;
  return (
    <div className="flex flex-wrap gap-1">
      {names.map((n) => (
        <span key={n} className="inline-flex items-center px-1.5 py-0.5 rounded-full bg-emerald-50 border border-emerald-200 text-[10px] text-emerald-700 font-medium">
          {n}
        </span>
      ))}
    </div>
  );
}

function Th({ children }) {
  return <th className="px-4 py-2.5 text-[11px] uppercase tracking-wider font-medium text-gray-500">{children}</th>;
}

// Inline-editable name cell. Read-only unless `canEdit` is true (super_admin).
// Save on Enter or check button; cancel on Escape or X button.
function NameCell({ user, canEdit, onSave }) {
  const [editing, setEditing] = useState(false);
  const [value, setValue] = useState(user.name || "");
  const [saving, setSaving] = useState(false);

  useEffect(() => { setValue(user.name || ""); }, [user.name]);

  const start = () => { setValue(user.name || ""); setEditing(true); };
  const cancel = () => { setValue(user.name || ""); setEditing(false); };
  const commit = async () => {
    setSaving(true);
    const ok = await onSave(user, value);
    setSaving(false);
    if (ok) setEditing(false);
  };

  if (!editing) {
    return (
      <div className="flex items-center gap-2 group">
        <span className="truncate">{user.name}</span>
        {canEdit && (
          <button
            type="button"
            onClick={start}
            className="opacity-0 group-hover:opacity-100 transition-opacity text-gray-400 hover:text-gray-700"
            title="Rename"
            data-testid={`user-name-edit-${user.email}`}
          >
            <PencilSimple size={12} />
          </button>
        )}
      </div>
    );
  }

  return (
    <div className="flex items-center gap-1.5">
      <Input
        autoFocus
        value={value}
        onChange={(e) => setValue(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === "Enter") commit();
          if (e.key === "Escape") cancel();
        }}
        disabled={saving}
        maxLength={80}
        className="h-7 text-sm w-44"
        data-testid={`user-name-input-${user.email}`}
      />
      <button
        type="button"
        onClick={commit}
        disabled={saving}
        className="p-1 rounded text-emerald-600 hover:bg-emerald-50 disabled:opacity-50"
        title="Save"
        data-testid={`user-name-save-${user.email}`}
      >
        <Check size={14} weight="bold" />
      </button>
      <button
        type="button"
        onClick={cancel}
        disabled={saving}
        className="p-1 rounded text-gray-500 hover:bg-gray-100 disabled:opacity-50"
        title="Cancel"
        data-testid={`user-name-cancel-${user.email}`}
      >
        <X size={14} weight="bold" />
      </button>
    </div>
  );
}
